import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@components';
import {
  nonRenderingPageTypes,
  pageTypeLookup,
  pageTypeTooltipLookup,
} from '@constants';
import { legacyDashboardURL } from '@helpers';
import {
  ExternalLinkIcon,
  MoreHorizontalIcon,
  ClipboardIcon,
  EditIcon,
} from '@svgs';
import './Page.scss';
import { GlobalContext } from '@contexts';
import { useContext } from 'react';
const Tag = ({ children, tooltip }) => {
  return (
    <Tooltip tooltip={tooltip}>
      <span className="sb-row-tag">{children}</span>
    </Tooltip>
  );
};

const getPageLink = (page, mode, business, site) => {
  // return the Link or a component that renders a link
  if (mode == 'template' && page.type == 'template_page') {
    return `/dashboard/projects/${business.id}/templates/${site.id}/pages/${page.id}`;
  }
  if (mode == 'content' && page.type == 'template_page') {
    return `/dashboard/sites/${site.id}/pages/${page.id}`;
  }
  return legacyDashboardURL(`/app/builder/${site.id}/${page.id}`);
};

export const Page = ({
  business,
  page,
  mode = 'content',
  readonly = false,
  site,
  templateNames,
  duplicate,
}) => {
  const { t, i18n } = useTranslation();
  // menu activation
  const dropdownMenu = useRef(null);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const globals = useContext(GlobalContext);
  const restrictPageTemplates = globals.group_settings?.restrict_page_templates;

  // determine if the user has access to the page
  // if there are no restricted page templates, then the user has access
  // if the page is a template page, then the user has access if the page type is in the restricted page templates
  const hasAccess =
    !restrictPageTemplates ||
    restrictPageTemplates.includes(page.custom_page_type);

  const closeOpenMenus = (e) => {
    if (showDropdownMenu && !dropdownMenu.current?.contains(e.target)) {
      setShowDropdownMenu(false);
    }
  };
  document.addEventListener('mousedown', closeOpenMenus);

  // link to the live page, will change depending on the current Site
  let livePageLink = `${site.formatted_base_url}${page.full_path}`;
  if (page.site_id != site.id) {
    // link to inherited page
    livePageLink = `${site.formatted_url.substring(0, site.formatted_url.length - 1)}${page.path}`;
  }

  const pageLink = getPageLink(page, mode, business, site);

  return (
    <div className="sb-row-item">
      {/**<input type="checkbox" className="sb-row-item-check"></input>**/}
      <div className="sb-row--page-name">
        <p>
          {(readonly || !hasAccess) && <span>{page.name}</span>}
          {!readonly && hasAccess && (
            <>
              {pageLink && pageLink.startsWith('/dashboard') && (
                <Link to={pageLink}>
                  <Tooltip tooltip="Edit this page">{page.name}</Tooltip>
                </Link>
              )}
              {pageLink && !pageLink.startsWith('/dashboard') && (
                <a href={pageLink} target="_blank">
                  <Tooltip tooltip="Open the page editor">{page.name}</Tooltip>
                </a>
              )}
            </>
          )}
        </p>
      </div>
      <div className="sb-row--page-full-path">
        {!nonRenderingPageTypes.includes(page.type) && (
          <>
            {site.type == 'template' && (
              <p>
                <span>/location-path</span>
                {page.path}
              </p>
            )}
            {page.site_id != site.id && (
              <p>
                {site.adjusted_base_directory.substring(
                  0,
                  site.adjusted_base_directory.length - 1,
                )}
                {page.path}
              </p>
            )}
            {site.type != 'template' && page.site_id == site.id && (
              <p>{page.full_path}</p>
            )}
          </>
        )}
      </div>
      <div className="sb-row--page-type">
        <p>
          {templateNames && page.type == 'template_page' && (
            <Tooltip
              tooltip={`Page built with a "${templateNames[page.custom_page_type]}" page template.`}
            >
              {templateNames[page.custom_page_type]}
            </Tooltip>
          )}
          {!templateNames && page.type == 'template_page' && <span>...</span>}
          {page.type != 'template_page' && (
            <Tooltip
              tooltip={
                pageTypeTooltipLookup[page.type] ||
                'Type of page. Determines how the page can be edited.'
              }
            >
              {pageTypeLookup[page.type]}
            </Tooltip>
          )}
        </p>
      </div>
      <div className="sb-row--page-tags">
        <p>
          {page.custom_field_published && (
            <Tag tooltip="Opt-in page. Page only published if opt-in conditions are met">
              Opt-in
            </Tag>
          )}
          {page.is_draft && (
            <Tag tooltip="Page content is in draft. Viewable in staging or preview">
              In draft
            </Tag>
          )}
          {page.active === false && (
            <Tag tooltip="Page is unpublished, will 404">Unpublished</Tag>
          )}
          {page.noindex === true && (
            <Tag tooltip="Page is Robots noindex, will not be indexed by search engines">
              Noindex
            </Tag>
          )}
        </p>
      </div>
      <div className="sb-options">
        <button
          className="sb-page-options-menu-trigger sb-options-trigger"
          onClick={() => setShowDropdownMenu(!showDropdownMenu)}
        >
          <MoreHorizontalIcon />
        </button>
        <div
          ref={dropdownMenu}
          className={
            'sb-options-menu ' + (showDropdownMenu ? 'sb-show' : 'sb-hide')
          }
        >
          <div className="sb-options-menu-content">
            {site.type != 'template' && (
              <a
                href={livePageLink}
                target="_blank"
                className="sb-options-item"
                title="Open this page on the live site"
              >
                <ExternalLinkIcon />
                {t('View page')}
              </a>
            )}
            {!readonly && hasAccess && (
              <>
                {page.is_draft && (
                  <a
                    href={`${site.preview_url.substring(0, site.preview_url.length - 1)}${page.path}`}
                    target="_blank"
                    className="sb-options-item"
                    title="Open this page draft on the preview site"
                  >
                    <ExternalLinkIcon />
                    {t('View draft page')}
                  </a>
                )}
                {pageLink && pageLink.startsWith('/dashboard') && (
                  <Link className="sb-options-item" to={pageLink}>
                    <EditIcon />
                    {t('Edit page')}
                  </Link>
                )}
                {pageLink && !pageLink.startsWith('/dashboard') && (
                  <a
                    className="sb-options-item"
                    href={pageLink}
                    target="_blank"
                  >
                    <ExternalLinkIcon />
                    {t('Open page editor')}
                  </a>
                )}
                {!!duplicate && (
                  <a
                    onClick={() => duplicate(page.id)}
                    target="_blank"
                    className="sb-options-item"
                    title="Duplicate this page"
                  >
                    <ClipboardIcon />
                    {t('Duplicate page')}
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
